import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import i18n from 'i18n';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { getCustomerPayments } from 'store/user-service/asyncActions';
import { getBillingHistory, getHasMoreBillingHistory } from 'store/user-service/selector';

import { NamespacesList } from 'core/enums/common';
import { BillingHistoryItem } from 'core/types/base';

import { ArrowPagination } from 'components/Profile/Subscription/ArrowPagination';
import {
	EmptyState,
	LoaderBox,
} from 'components/Profile/Subscription/UpdatePaymentInformation/styled';
import { SharedModal } from 'components/shared/Modal';

import { ReactComponent as Card } from 'assets/icons/card.svg';
import { ReactComponent as Check } from 'assets/icons/check.svg';
import { ReactComponent as Download } from 'assets/icons/download.svg';
import { ReactComponent as Failure } from 'assets/icons/failure.svg';

import { formatDate, tableHeader } from './helpers';
import * as Styled from './styled';

interface BillingHistoryModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const BillingHistoryModal: FC<BillingHistoryModalProps> = ({ isOpen, onClose }) => {
	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const [billingHistoryLoading, setBillingHistoryLoading] = useState(false);
	const [gettingStartHistory, setGettingStartHistory] = useState(false);
	const [gettingEndHistory, setGettingEndHistory] = useState(false);

	const billingHistory = useSelector(getBillingHistory);
	const hasMoreHistory = useSelector(getHasMoreBillingHistory);

	const dispatch = useAppDispatch();

	const textAlign = useCallback(
		(index = 1) => {
			if (index === 0) {
				return i18n?.language === 'ar' ? 'right' : 'left';
			}
			return i18n?.language === 'ar' ? 'left' : 'right';
		},
		[i18n?.language]
	);

	const handleGetCustomerPayments = async () => {
		setBillingHistoryLoading(true);
		await dispatch(getCustomerPayments({ recordsPerPage: 5 })).unwrap();
		setBillingHistoryLoading(false);
	};

	const getOperationStatus = (status: string) => {
		switch (status) {
			case 'succeeded':
				return <Check />;
			case 'canceled':
				return <Failure />;
			case 'processing':
				return (
					<Oval
						height={14}
						width={14}
						color="rgba(0, 0, 0, 0.12)"
						wrapperStyle={{}}
						wrapperClass=""
						visible={true}
						ariaLabel="oval-loading"
						secondaryColor="rgba(0, 0, 0, 0.12)"
						strokeWidth={5}
						strokeWidthSecondary={5}
					/>
				);
			case 'requires_payment_method':
				return (
					<Tooltip
						componentsProps={{
							tooltip: {
								sx: {
									backgroundColor: '#f5f5f9',
									color: 'rgba(0, 0, 0, 0.87)',
									fontSize: '12px',
									maxWidth: 200,
									textAlign: 'center',
									marginBottom: '5px',
									border: '1px solid #dadde9',
									'& .MuiTooltip-popper': { margin: 0 },
								},
							},
							popper: {
								sx: {
									marginBottom: '5px',
								},
							},
						}}
						PopperProps={{
							modifiers: [
								{
									name: 'offset',
									options: {
										offset: [0, 0],
									},
								},
							],
						}}
						title={commonTranslate('tooltip.somethingHappenedWithCard')}
						placement="top"
					>
						<Failure />
					</Tooltip>
				);
			default:
				return <Failure />;
		}
	};

	const handleCloseModal = () => {
		onClose();
	};

	useEffect(() => {
		if (isOpen) {
			handleGetCustomerPayments();
		}
	}, [isOpen]);

	const checkingBillingHistory = useMemo(
		() => !billingHistoryLoading && (billingHistory?.length || 0) > 0,
		[billingHistoryLoading, billingHistory]
	);

	const handleGetStartAfterHistory = async () => {
		if (billingHistory) {
			setGettingStartHistory(true);
			await dispatch(
				getCustomerPayments({
					recordsPerPage: 5,
					startAfter: billingHistory[billingHistory.length - 1].id,
				})
			).unwrap();
			setGettingStartHistory(false);
		}
	};

	const handleGetEndBeforeHistory = async () => {
		if (billingHistory) {
			setGettingEndHistory(true);
			await dispatch(
				getCustomerPayments({
					recordsPerPage: 5,
					endBefore: billingHistory[0].id,
				})
			).unwrap();
			setGettingEndHistory(false);
		}
	};

	return (
		<SharedModal
			isOpen={isOpen}
			handleClose={handleCloseModal}
			sx={{ width: '100% !important', maxWidth: '815px' }}
		>
			<Styled.ModalContent>
				<Styled.ModalTitle>{modalTranslate('billingHistoryModal.title')}</Styled.ModalTitle>
				<TableContainer
					sx={{
						maxHeight: 500,
						minHeight: checkingBillingHistory ? '250px' : 'inherit',
					}}
				>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								{tableHeader.map((header, index) => (
									<TableCell
										key={header}
										sx={{ borderBottom: 'none', padding: '0' }}
										align={textAlign(index)}
									>
										<Styled.TableTitle>{modalTranslate(header)}</Styled.TableTitle>
									</TableCell>
								))}
							</TableRow>
						</TableHead>

						{checkingBillingHistory && (
							<TableBody>
								{billingHistory?.map((item: BillingHistoryItem) => (
									<TableRow key={item.date}>
										<TableCell
											align={textAlign(0)}
											sx={{ borderBottom: 'none', padding: '16px 0 0' }}
										>
											{item.product || 'Billing Operation'}
										</TableCell>
										<TableCell
											align={textAlign()}
											sx={{ borderBottom: 'none', padding: '16px 0 0' }}
										>
											{formatDate(item.date)}
										</TableCell>
										<TableCell
											align={textAlign()}
											sx={{ borderBottom: 'none', padding: '16px 0 0' }}
										>
											{item.amount}$
										</TableCell>
										<TableCell
											align={textAlign()}
											sx={{ borderBottom: 'none', padding: '16px 0 0' }}
										>
											{getOperationStatus(item.status)}
										</TableCell>
										<TableCell
											align={textAlign()}
											sx={{ borderBottom: 'none', padding: '16px 0 0' }}
										>
											<Tooltip
												componentsProps={{
													tooltip: {
														sx: {
															backgroundColor: '#f5f5f9',
															color: 'rgba(0, 0, 0, 0.87)',
															fontSize: '12px',
															maxWidth: 200,
															textAlign: 'center',
															marginBottom: '5px',
															border: '1px solid #dadde9',
															'& .MuiTooltip-popper': { margin: 0 },
														},
													},
													popper: {
														sx: {
															marginBottom: '5px',
														},
													},
												}}
												PopperProps={{
													modifiers: [
														{
															name: 'offset',
															options: {
																offset: [0, 0],
															},
														},
													],
												}}
												title={
													!item.downloadLink
														? commonTranslate('tooltips.disabledDownloadButton')
														: null
												}
												placement="top"
											>
												<Box
													sx={{
														margin: i18n.language === 'ar' ? '0 auto 0 0' : '0 0 0 auto',
														width: '40px',
													}}
												>
													<Styled.DownloadButton
														disabled={!item.downloadLink}
														onClick={() => {
															window.open(item?.downloadLink || '', '_blank', 'noreferrer');
														}}
													>
														<Download />
													</Styled.DownloadButton>
												</Box>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						)}
					</Table>
				</TableContainer>
				{billingHistoryLoading ? (
					<LoaderBox>
						<Oval
							height={34}
							width={34}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</LoaderBox>
				) : (
					billingHistory?.length === 0 && (
						<EmptyState>
							<Card />
							<Typography variant="h3">
								{modalTranslate('billingHistoryModal.emptyStateTitle')}
							</Typography>
							<Typography>{modalTranslate('billingHistoryModal.emptyStateDescription')}</Typography>
						</EmptyState>
					)
				)}
				{checkingBillingHistory && (
					<ArrowPagination
						isLoadingEnd={gettingEndHistory}
						isLoadingStart={gettingStartHistory}
						hasMoreEnd={hasMoreHistory?.end || false}
						hasMoreStart={hasMoreHistory?.start || false}
						onEnd={handleGetEndBeforeHistory}
						onStart={handleGetStartAfterHistory}
					/>
				)}
			</Styled.ModalContent>
		</SharedModal>
	);
};
